import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps<T extends string> {
  readonly value: T | '' | null | undefined;
  readonly options: readonly SelectOption<T>[];
  onChange(value: T): void;
  /** Shown while nothing is chosen. */
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly className?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
  readonly 'aria-describedby'?: string;
  readonly required?: boolean;
}

const GAP = 6;
const MAX_HEIGHT = 288;
const TYPEAHEAD_MS = 600;

/**
 * The app's own select. The native one opened the operating system's menu, which
 * matched nothing else on the page. This is the "select-only combobox" pattern:
 * focus stays on the button, the list follows it with `aria-activedescendant`.
 * The list is portalled to `<body>` and placed by the button, so a card with
 * `overflow: hidden` does not clip it.
 */
export function Select<T extends string>(props: SelectProps<T>): JSX.Element {
  const { value, options, onChange, placeholder, disabled = false, className = '' } = props;
  const autoId = useId();
  const id = props.id ?? autoId;
  const listId = `${id}-listbox`;
  const optionId = (index: number): string => `${id}-option-${index}`;
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState<CSSProperties | null>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const enabled = (index: number): boolean =>
    index >= 0 && index < options.length && !options[index]!.disabled;
  const step = (from: number, delta: 1 | -1): number => {
    for (let index = from + delta; index >= 0 && index < options.length; index += delta)
      if (enabled(index)) return index;
    return enabled(from) ? from : -1;
  };
  const first = (): number => step(-1, 1);
  const last = (): number => step(options.length, -1);

  const openMenu = (start = selectedIndex): void => {
    if (disabled) return;
    setActive(enabled(start) ? start : first());
    setOpen(true);
  };
  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };
  const choose = (index: number): void => {
    const option = options[index];
    if (!option || option.disabled) return;
    close();
    if (option.value !== value) onChange(option.value);
  };

  const place = useCallback((): void => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom - GAP * 2;
    const above = rect.top - GAP * 2;
    const up = below < Math.min(MAX_HEIGHT, 180) && above > below;
    setPosition({
      position: 'fixed',
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      minWidth: rect.width,
      maxWidth: Math.max(rect.width, window.innerWidth - 16),
      maxHeight: Math.max(120, Math.min(MAX_HEIGHT, up ? above : below)),
      ...(up ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const outside = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (trigger.current?.contains(target) || menu.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('pointerdown', outside);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('pointerdown', outside);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open || active < 0) return;
    // jsdom has no scrollIntoView; browsers keep the active option in view.
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const typeahead = (key: string): void => {
    const now = Date.now();
    const text =
      (now - typed.current.at < TYPEAHEAD_MS ? typed.current.text : '') + key.toLowerCase();
    typed.current = { text, at: now };
    const start = open ? active : selectedIndex;
    // A new first letter moves past the current option; a longer prefix may stay on it.
    const offset = text.length > 1 ? 0 : 1;
    for (let i = 0; i < options.length; i++) {
      const index = (Math.max(start, 0) + offset + i) % options.length;
      if (enabled(index) && options[index]!.label.toLowerCase().startsWith(text)) {
        if (open) setActive(index);
        else openMenu(index);
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const { key } = event;
    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        openMenu();
      } else if (key.length === 1 && /\S/u.test(key)) typeahead(key);
      return;
    }
    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((current) => step(current, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((current) => step(current, -1));
        break;
      case 'Home':
      case 'PageUp':
        event.preventDefault();
        setActive(first());
        break;
      case 'End':
      case 'PageDown':
        event.preventDefault();
        setActive(last());
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(active);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (key.length === 1 && /\S/u.test(key)) typeahead(key);
    }
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-required={props.required || undefined}
        disabled={disabled}
        className={`kitup-input kitup-select ${className}`}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={`kitup-select-value${selected ? '' : ' kitup-select-placeholder'}`}>
          {selected?.label ?? placeholder ?? ''}
        </span>
        <svg aria-hidden="true" viewBox="0 0 20 20" className="kitup-select-chevron">
          <path
            d="m6 8 4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && position
        ? createPortal(
            <ul
              ref={menu}
              id={listId}
              role="listbox"
              aria-label={props['aria-label']}
              aria-labelledby={props['aria-labelledby']}
              className="kitup-select-menu"
              style={position}
              // Keep the focus on the button: it owns the keyboard while the list is open.
              onMouseDown={(event) => event.preventDefault()}
            >
              {options.map((option, index) => (
                <li
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === selectedIndex}
                  aria-disabled={option.disabled || undefined}
                  data-active={index === active || undefined}
                  className="kitup-select-option"
                  onPointerMove={() => {
                    if (!option.disabled && active !== index) setActive(index);
                  }}
                  onClick={() => choose(index)}
                >
                  <span>{option.label}</span>
                  {index === selectedIndex ? (
                    <svg aria-hidden="true" viewBox="0 0 20 20" className="kitup-select-check">
                      <path
                        d="m5 10.5 3.2 3.2L15 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </>
  );
}
